import { useCallback, useEffect, useState } from 'react';
import { IAssistCard } from '../../redux/features/assistRequest/assistRequest.props';
import { isAssistCardActive } from '../../redux/features/assistRequest/assistRequest.slice';
import {
  assistClipStore,
  getAssistClipPlayer,
  playAssistClip,
} from '../../utils/assistRequest/hearAndConfirm/clip';
import { assistCardLogFields } from '../../utils/assistRequest/logFields';
import { assistPlaybackRate } from '../../utils/assistRequest/types';

/**
 * HITL-1786 (HITL 8.0.0): the card's pushed clip (contract D11).
 *
 * The card never waits for it. Until `useAssistPool` holds a clip for this
 * card, `clip` is null and the card says there is no audio yet; then Play
 * shows. The clip plays on its own as it arrives (`useAssistPool`, after the
 * arrival cue), at the request's `playback_rate` with the pitch kept; Play
 * replays it at the same rate, and is the way in when the browser refused the
 * autoplay. Playback stops when the card ends, and the clip is dropped with
 * the card.
 */
const useAssistClip = (card: IAssistCard) => {
  const { requestId } = card;
  const rate = assistPlaybackRate(card.request?.playback_rate);
  const [clip, setClip] = useState(() => assistClipStore.get(requestId));
  const active = isAssistCardActive(card);

  useEffect(() => {
    setClip(assistClipStore.get(requestId));
    const unsubscribe = assistClipStore.subscribe(() =>
      setClip(assistClipStore.get(requestId))
    );
    return () => {
      unsubscribe();
      getAssistClipPlayer().stop();
      assistClipStore.drop(requestId);
    };
  }, [requestId]);

  useEffect(() => {
    if (!active) getAssistClipPlayer().stop();
  }, [active]);

  const play = useCallback(async () => {
    if (!clip) return false;
    return playAssistClip(clip, rate, { logFields: assistCardLogFields(card) });
    // card is read for its log fields only; requestId keys the clip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip, rate, requestId]);

  return { clip, play };
};

export default useAssistClip;
